import { useQuery } from "@tanstack/react-query";
import { notification } from "antd";

import { mapPointMetaQueryToColumnData, mapQuestionnairePointToColumnData } from "@Components/activity/helpers/mappers";
import { QueryKeys, ThreeMinutes } from "@Constants";
import SessionManager from "@Core/session";

import { IActivityDetails } from "@Models/IActivityDetails";
import ActivityService from "@Services/ActivityService";
import QuestionnaireService from "@Services/QuestionnaireService";
import { PointsTypes } from "@Models/IPoints";

/**
 * Gets points metadata for the specified user id.
 */
export const useCertificateTableDataQuery = (activityInstanceIds: string[] = []) => {
    const activityService = new ActivityService();
    const questionnaireService = new QuestionnaireService();
    const { userId } = SessionManager.getServiceUser();

    const cpdTableDataQuery = useQuery(
        [QueryKeys.PointsTableData, PointsTypes.CPD, userId],
        async () => {
            const pointsMetadataResult = await activityService.getPointsMetadata(userId);
            if (pointsMetadataResult.hasErrors) {
                return [];
            }


            return mapPointMetaQueryToColumnData(pointsMetadataResult.value);
        },
        {
            enabled: !!userId,
            placeholderData: [],
            refetchOnWindowFocus: false,
            staleTime: ThreeMinutes,
        }
    );

    const cmeTableDataQuery = useQuery(
        [QueryKeys.PointsTableData, PointsTypes.CME, userId, activityInstanceIds],
        async () => {
            const questionnairePointsResult = await questionnaireService.getPoints(activityInstanceIds);
            if (questionnairePointsResult.hasErrors) {
                return [];
            }
            return mapQuestionnairePointToColumnData(questionnairePointsResult.value.points);
        },
        {
            enabled: !!userId && !!activityInstanceIds,
            placeholderData: [],
            refetchOnWindowFocus: false,
            staleTime: ThreeMinutes,
        }
    );

    return {
        data: {
            hasCPDReports: (cpdTableDataQuery.data?.length ?? 0) > 0,
            hasCMEReports: (cmeTableDataQuery.data?.length ?? 0) > 0,
            tableData: [...(cpdTableDataQuery.data ?? []), ...(cmeTableDataQuery.data ?? [])].sort((a, b) =>
                b.creationDate.localeCompare(a.creationDate)
            ),
        },
        isHydrated: cpdTableDataQuery.isFetched && cmeTableDataQuery.isFetched,
        isFetching: cpdTableDataQuery.isFetching || cmeTableDataQuery.isFetching,
        isLoading: cpdTableDataQuery.isLoading || cmeTableDataQuery.isLoading,
    };
};

/**
 * Gets points report for the specified points metadata id and user id.
 *
 * (not currently used)
 */
export const useGetPointsReportQuery = (pointsMetadataId: string, userId = SessionManager.getServiceUser()?.userId) => {
    const service = new ActivityService();
    return useQuery(
        [QueryKeys.PointsReport, userId, pointsMetadataId],
        async () => {
            const result = await service.getPointsReport(pointsMetadataId, userId);
            return !result.hasErrors ? result.value : [];
        },
        {
            enabled: !!pointsMetadataId,
            placeholderData: [],
            refetchOnWindowFocus: false,
            staleTime: ThreeMinutes,
        }
    );
};

/**
 * Gets Current Activity Details
 */
export const useCurrentActivityQuery = (activityId: string, queryKey: string, callBack?: () => void) => {
    const isPublic = queryKey === QueryKeys.ActivityCurrentPublic;
    return useQuery<IActivityDetails>(
        [queryKey, activityId],
        async () => {
            if (!activityId) return null; // in case manually refetched
            const service = new ActivityService();
            const result = await service.getActivities([activityId], isPublic);

            if (!result.hasErrors && result.value.length > 0) {
                return result.value[0];
            }

            if (result.hasErrors) {
                notification.error({
                    message: "We were unable to find this activity.",
                });
                callBack?.();
            }

            return null;
        },
        {
            enabled: !!activityId,
            refetchOnWindowFocus: false,
            staleTime: ThreeMinutes,
        }
    );
};
